import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import MarketingHeader from "@/components/landing/MarketingHeader";
import ComplianceFooter from "@/components/landing/ComplianceFooter";
import { ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import { GreenTrackLink, Eyebrow } from "@/components/ui/system-primitives";
import "./legal.css";

/**
 * LegalPage — public renderer for the 5 markdown legal documents.
 *
 * Layout follows the design-system mandate:
 *   • Sticky left TOC using the Green Track active state.
 *   • 47/56 Inter H1 + 24/32 Inter H2 (via legal.css) matching the SaaS scale.
 *   • Related-documents chip row retained below the article as a fallback.
 */

const DOC_MAP = {
    privacy:            { file: "privacy.md",         title: "Privacy Policy",                       testid: "legal-privacy" },
    terms:              { file: "terms.md",           title: "Terms of Service",                     testid: "legal-terms"   },
    cookies:            { file: "cookies.md",         title: "Cookie Policy",                        testid: "legal-cookies" },
    disclaimer:         { file: "disclaimer.md",      title: "Disclaimer & Limitation of Liability", testid: "legal-disclaimer" },
    "risk-disclosure":  { file: "risk-disclosure.md", title: "Risk Disclosure",                      testid: "legal-risk-disclosure" },
};

export default function LegalPage() {
    const { slug } = useParams();
    const nav = useNavigate();
    const doc = DOC_MAP[slug];
    const [md, setMd] = useState("");
    const [err, setErr] = useState(null);

    useEffect(() => {
        if (!doc) return;
        let cancelled = false;
        (async () => {
            try {
                const [res, cfg] = await Promise.all([
                    fetch(`/legal/${doc.file}`),
                    api.get("/paybill/config").catch(() => null),
                ]);
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                let text = await res.text();
                const c = cfg?.data || {};
                const tokens = {
                    "{{PAYBILL_NUMBER}}": c.paybill_number || "",
                    "{{ACCOUNT_NUMBER}}": c.account_number || "",
                    "{{ACCOUNT_NAME}}":   c.account_name   || "Melian Dialogue Limited",
                };
                Object.entries(tokens).forEach(([tok, val]) => {
                    text = text.split(tok).join(val);
                });
                if (!cancelled) setMd(text);
            } catch (e) {
                if (!cancelled) setErr("This document could not be loaded.");
                if (process.env.NODE_ENV !== "production") console.debug(e);
            }
        })();
        return () => { cancelled = true; };
    }, [doc]);

    if (!doc) {
        return (
            <div className="min-h-screen flex flex-col">
                <MarketingHeader testidPrefix="legal" showNav showLogin />
                <main className="flex-1 max-w-3xl mx-auto px-6 py-24 text-center">
                    <h1 className="font-heading text-h1 mb-2">Document not found</h1>
                    <p className="font-body text-body-lg text-muted-foreground">
                        Choose a document from the list below.
                    </p>
                    <ul className="mt-6 space-y-2">
                        {Object.entries(DOC_MAP).map(([k, v]) => (
                            <li key={k}>
                                <Link className="text-brand-green hover:underline" to={`/legal/${k}`}>
                                    {v.title}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </main>
                <ComplianceFooter />
            </div>
        );
    }

    return (
        <div className="min-h-screen flex flex-col" data-testid={doc.testid}>
            <MarketingHeader testidPrefix="legal" showNav showLogin />

            <main className="flex-1 max-w-6xl w-full mx-auto px-6 py-12 md:py-16">
                <Link
                    to="/"
                    className="inline-flex items-center gap-1 text-caption text-muted-foreground hover:text-brand-green mb-6"
                    data-testid="legal-back-home"
                >
                    <ArrowLeft width={14} height={14} strokeWidth={1.75} /> Back to home
                </Link>

                <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-10 md:gap-14">
                    {/* Sticky Green-Track TOC (desktop) */}
                    <aside className="hidden md:block" aria-label="Legal documents">
                        <div className="sticky top-24">
                            <Eyebrow className="mb-3 !text-caption" tone="green" testid="legal-toc-eyebrow">
                                Compliance Documents
                            </Eyebrow>
                            <nav className="space-y-1" data-testid="legal-toc">
                                {Object.entries(DOC_MAP).map(([k, v]) => (
                                    <GreenTrackLink
                                        key={k}
                                        href={`/legal/${k}`}
                                        active={k === slug}
                                        testid={`legal-toc-${k}`}
                                        onClick={(e) => { e.preventDefault(); nav(`/legal/${k}`); }}
                                    >
                                        {v.title}
                                    </GreenTrackLink>
                                ))}
                            </nav>
                        </div>
                    </aside>

                    {/* Article */}
                    <article className="legal-doc min-w-0" data-testid="legal-content">
                        {err ? (
                            <p className="text-destructive font-body">{err}</p>
                        ) : md ? (
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>{md}</ReactMarkdown>
                        ) : (
                            <p className="text-muted-foreground font-body text-body-base">Loading document…</p>
                        )}

                        {/* Mobile-only related-docs chip row */}
                        <div className="md:hidden mt-10 border-t border-border pt-6">
                            <Eyebrow className="mb-3 !text-caption" tone="green">
                                Related Documents
                            </Eyebrow>
                            <ul className="flex flex-wrap gap-2">
                                {Object.entries(DOC_MAP).filter(([k]) => k !== slug).map(([k, v]) => (
                                    <li key={k}>
                                        <Link
                                            to={`/legal/${k}`}
                                            className="text-caption font-heading font-medium px-3 py-1.5 rounded-full border border-border hover:border-brand-green hover:text-brand-green transition-colors"
                                            data-testid={`legal-related-${k}`}
                                        >
                                            {v.title}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </article>
                </div>
            </main>

            <ComplianceFooter />
        </div>
    );
}
